import React, { useState, useEffect, useCallback } from 'react';
import './BonusGate.css';
import { money } from '../utils/money';

const API = '';

function formatM(val) {
  const n = parseFloat(val) || 0;
  if (Math.abs(n) >= 1000000) {
    return 'RM ' + (n / 1000000).toFixed(3) + 'M';
  }
  if (Math.abs(n) >= 1000) {
    return 'RM ' + Math.round(n / 1000) + 'k';
  }
  return 'RM ' + Math.round(n);
}

function formatFull(val) {
  return money(parseFloat(val) || 0);
}

const MONTH_SHORT = {
  '2026-01': 'Jan', '2026-02': 'Feb', '2026-03': 'Mar',
  '2026-04': 'Apr', '2026-05': 'May', '2026-06': 'Jun',
  '2026-07': 'Jul', '2026-08': 'Aug', '2026-09': 'Sep',
  '2026-10': 'Oct', '2026-11': 'Nov', '2026-12': 'Dec',
};

function gateStatusLabel(status) {
  switch (status) {
    case 'met': return 'GATE PASSED';
    case 'not_met': return 'GATE NOT MET';
    case 'incomplete': return 'IN PROGRESS';
    case 'no_target': return 'NO TARGET';
    default: return 'NO DATA';
  }
}

function gateIcon(status) {
  switch (status) {
    case 'met': return '\u{1F513}';
    case 'not_met': return '\u{1F512}';
    case 'incomplete': return '\u{1F552}';
    case 'no_target': return '\u{26A0}\u{FE0F}';
    default: return '\u{1F6AB}';
  }
}

export default function BonusGate() {
  const [quarterly, setQuarterly] = useState(null);
  const [yearly, setYearly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editTargets, setEditTargets] = useState({});
  const [releasing, setReleasing] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [qRes, yRes] = await Promise.all([
        fetch(`${API}/api/bonus/quarterly`, { headers }),
        fetch(`${API}/api/bonus/yearly`, { headers }),
      ]);
      if (!qRes.ok) throw new Error('Failed to fetch quarterly data');
      if (!yRes.ok) throw new Error('Failed to fetch yearly data');
      const qData = await qRes.json();
      const yData = await yRes.json();
      setQuarterly(qData.quarters);
      setYearly(yData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTargetChange = (qKey, value) => {
    setEditTargets(prev => ({ ...prev, [qKey]: value }));
  };

  const saveTarget = async (qKey) => {
    const val = parseFloat(editTargets[qKey]);
    if (isNaN(val)) return;
    try {
      const res = await fetch(`${API}/api/bonus/quarterly/target`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quarter: qKey, ebitda_target: val }),
      });
      if (!res.ok) throw new Error('Failed to save target');
      setEditTargets(prev => {
        const next = { ...prev };
        delete next[qKey];
        return next;
      });
      await fetchData();
    } catch (err) {
      alert('Error saving target: ' + err.message);
    }
  };

  const toggleRelease = async (qKey, currentlyReleased) => {
    setReleasing(qKey);
    try {
      const res = await fetch(`${API}/api/bonus/quarterly/release`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quarter: qKey, released: !currentlyReleased }),
      });
      if (!res.ok) throw new Error('Failed to update release status');
      await fetchData();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setReleasing(null);
    }
  };

  if (loading) {
    return <div className="bonus-loading">Loading bonus data...</div>;
  }

  if (error) {
    return <div className="bonus-error">Error: {error}</div>;
  }

  return (
    <div className="bonus-gate-page">
      <h1>Quarterly Bonus Gate</h1>
      <p className="page-subtitle">EBITDA-gated quarterly bonus release and yearly tier projection</p>

      {/* Section 1: Quarterly EBITDA Gates */}
      <h2 className="bonus-section-title">Quarterly EBITDA Gates</h2>
      <div className="bonus-quarters-grid">
        {quarterly && quarterly.map(q => {
          const pct = q.ebitda_target > 0 ? Math.min((q.quarter_total / q.ebitda_target) * 100, 100) : 0;
          const editVal = editTargets[q.key];
          const targetDisplay = editVal !== undefined ? editVal : q.ebitda_target;

          return (
            <div key={q.key} className={`bonus-quarter-card gate-${q.gate_status}`}>
              <div className="bonus-quarter-header">
                <span className="bonus-quarter-label">{q.label}</span>
                <span className="bonus-quarter-months">
                  {q.months.map(m => MONTH_SHORT[m.month] || m.label).join(' - ')}
                </span>
              </div>

              {/* Target input */}
              <div className="bonus-target-row">
                <label>EBITDA Target</label>
                <div className="bonus-target-input-wrap">
                  <input
                    type="number"
                    className="bonus-target-input"
                    value={targetDisplay}
                    onChange={e => handleTargetChange(q.key, e.target.value)}
                    placeholder="0"
                  />
                  {editVal !== undefined && (
                    <button className="bonus-target-save" onClick={() => saveTarget(q.key)}>
                      Save
                    </button>
                  )}
                </div>
              </div>

              {/* Monthly breakdown */}
              <table className="bonus-monthly-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>EBITDA</th>
                  </tr>
                </thead>
                <tbody>
                  {q.months.map(m => (
                    <tr key={m.month}>
                      <td>{MONTH_SHORT[m.month] || m.label}</td>
                      <td className={m.ebitda === null ? 'no-data' : ''}>
                        {m.ebitda !== null ? formatM(m.ebitda) : '--'}
                      </td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td>Total</td>
                    <td>{formatM(q.quarter_total)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Progress bar */}
              <div className="bonus-progress-wrap">
                <div className="bonus-progress-bar">
                  <div
                    className={`bonus-progress-fill ${q.gate_status}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="bonus-progress-label">
                  {q.ebitda_target > 0 ? `${pct.toFixed(1)}% of target` : 'No target set'}
                </span>
              </div>

              {/* Gate status */}
              <div className={`bonus-gate-status ${q.gate_status}`}>
                <span className="bonus-gate-icon">{gateIcon(q.gate_status)}</span>
                <span>{gateStatusLabel(q.gate_status)}</span>
              </div>

              {/* Release button */}
              {q.bonus_released ? (
                <button
                  className="bonus-release-btn revoke"
                  onClick={() => toggleRelease(q.key, true)}
                  disabled={releasing === q.key}
                >
                  {releasing === q.key ? 'Updating...' : 'Revoke Bonus'}
                </button>
              ) : (
                <button
                  className="bonus-release-btn release"
                  onClick={() => toggleRelease(q.key, false)}
                  disabled={q.gate_status !== 'met' || releasing === q.key}
                >
                  {releasing === q.key ? 'Updating...' : 'Release Bonus'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Section 2: Yearly Bonus Projection */}
      <h2 className="bonus-section-title">Yearly Bonus Projection</h2>
      {yearly && (
        <div className="bonus-yearly-card">
          <div className="bonus-yearly-grid">
            <div className="bonus-yearly-summary">
              <div className="bonus-yearly-kpi">
                <span className="bonus-yearly-kpi-label">YTD EBITDA</span>
                <span className="bonus-yearly-kpi-value">{formatFull(yearly.ytd_ebitda)}</span>
              </div>
              <div className="bonus-yearly-kpi">
                <span className="bonus-yearly-kpi-label">Months Completed</span>
                <span className="bonus-yearly-kpi-value">{yearly.months_completed} / 12</span>
              </div>
              <div className="bonus-yearly-kpi">
                <span className="bonus-yearly-kpi-label">Annualized Projection</span>
                <span className="bonus-yearly-kpi-value accent">{formatM(yearly.annualized_projection)}</span>
              </div>
              <div className="bonus-yearly-kpi">
                <span className="bonus-yearly-kpi-label">Current Tier</span>
                <span className="bonus-yearly-kpi-value">
                  {yearly.current_tier ? yearly.current_tier.tier_name : 'Below T1'}
                </span>
              </div>
            </div>

            <div>
              <table className="bonus-tier-table">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Profit Threshold</th>
                    <th>Bonus Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {yearly.tiers.map(t => (
                    <tr
                      key={t.tier_name}
                      className={yearly.current_tier && yearly.current_tier.tier_name === t.tier_name ? 'active-tier' : ''}
                    >
                      <td>
                        {yearly.current_tier && yearly.current_tier.tier_name === t.tier_name && (
                          <span className="bonus-tier-marker" />
                        )}
                        {t.tier_name}
                      </td>
                      <td>{formatM(t.profit_threshold)}</td>
                      <td>{t.bonus_rate.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="bonus-estimated">
                <span className="bonus-estimated-label">Estimated Bonus Pool</span>
                <span className="bonus-estimated-value">{formatFull(yearly.estimated_bonus_pool)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
